import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Calendar, Clock, CheckCircle2, AlertCircle, MapPin, User, Dumbbell, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import { format, addDays } from "date-fns";
import { tr } from "date-fns/locale";
import { getDateLocale } from "@/utils/dateUtils";
import TimePicker from "./TimePicker";
import { useLocalizedData } from "@/contexts/LocalizedDataContext";
import { useTranslation } from "react-i18next";

export default function MemberCreateRequest() {
  const { t } = useTranslation(["common", "dashboard", "members", "trainers", "reservations", "settings", "payments", "rooms", "messaging", "business", "memberDashboard", "packages"]);
  const { getLocalizedBranch, getLocalizedTrainingType } = useLocalizedData();

  const [memberships, setMemberships] = useState([]);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [selectedPackageInfo, setSelectedPackageInfo] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [filteredTrainers, setFilteredTrainers] = useState([]);
  const [filteredRooms, setFilteredRooms] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [selectedDate, setSelectedDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [selectedTime, setSelectedTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [notes, setNotes] = useState("");
  const [roomAvailability, setRoomAvailability] = useState(null);
  const [trainerAvailability, setTrainerAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [businessSettings, setBusinessSettings] = useState({});
  const [businessWorkingHours, setBusinessWorkingHours] = useState({});
  
  // New states for branch and demo selection
  const [businessBranches, setBusinessBranches] = useState([]);
  const [branchDemoDurations, setBranchDemoDurations] = useState({});
  const [selectedBranch, setSelectedBranch] = useState("");
  const [reservationType, setReservationType] = useState(""); // "package" or "demo"

  // Generate time slots
  const timeSlots = [];
  for (let h = 6; h < 23; h++) {
    for (let m = 0; m < 60; m += 30) {
      timeSlots.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  // Generate date options (next 14 days)
  const dateOptions = [];
  for (let i = 0; i < 14; i++) {
    const date = addDays(new Date(), i);
    dateOptions.push({
      value: format(date, "yyyy-MM-dd"),
      label: format(date, "d MMMM yyyy, EEEE", { locale: getDateLocale() })
    });
  }

  useEffect(() => {
    fetchMemberships();
  }, []);

  const fetchMemberships = async () => {
    try {
      const response = await fetch(`${API}/members/me/memberships`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setMemberships(data);
        if (data.length === 1) {
          handleMembershipSelect(data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching memberships:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleMembershipSelect = async (membership) => {
    setSelectedMembership(membership);
    setSelectedPackageId("");
    setSelectedPackageInfo(null);
    setSelectedTrainer("");
    setSelectedRoom("");

    // Fetch packages for this membership
    try {
      const response = await fetch(
        `${API}/members/${membership.member_id}/packages`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        const activePackages = data.filter(
          (p) => p.status !== "cancelled" && p.is_active !== false && p.remaining_sessions > 0
        );
        setPackages(activePackages);
      }

      // Fetch trainers and rooms for this business
      const [trainersRes, roomsRes, businessRes] = await Promise.all([
        fetch(`${API}/businesses/${membership.business_id}/trainers`, { credentials: "include" }),
        fetch(`${API}/businesses/${membership.business_id}/rooms`, { credentials: "include" }),
        fetch(`${API}/businesses/${membership.business_id}`, { credentials: "include" })
      ]);

      if (trainersRes.ok) {
        const t = await trainersRes.json();
        setTrainers(t.filter(tr => tr.is_active !== false));
      }
      if (roomsRes.ok) {
        setRooms(await roomsRes.json());
      }
      if (businessRes.ok) {
        const businessData = await businessRes.json();
        setBusinessSettings(businessData);
        setBusinessWorkingHours(businessData.working_hours || {});
        // Load business branches and demo durations
        setBusinessBranches(businessData.branches || []);
        setBranchDemoDurations(businessData.branch_demo_durations || {});
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  // Filter packages by selected branch
  const filteredPackages = selectedBranch 
    ? packages.filter(p => {
        const pkgBranches = p.package_branches || (p.package_branch ? [p.package_branch] : []);
        return pkgBranches.includes(selectedBranch) || pkgBranches.length === 0;
      })
    : packages;

  // When branch changes, reset package selection
  useEffect(() => {
    if (selectedBranch) {
      setSelectedPackageId("");
      setSelectedPackageInfo(null);
      setReservationType("");
    }
  }, [selectedBranch]);

  // Package selection - auto fill branch and filter trainers/rooms
  useEffect(() => {
    if (reservationType === "package" && selectedPackageId && packages.length > 0) {
      const pkg = packages.find((p) => p.member_package_id === selectedPackageId);
      if (pkg) {
        setSelectedPackageInfo(pkg);
        const branch = selectedBranch || pkg.package_branches?.[0] || pkg.package_branch || "";

        // Filter trainers by branch
        if (branch) {
          const ft = trainers.filter((t) => {
            if (t.branches && t.branches.length > 0) {
              return t.branches.includes(branch);
            }
            return true;
          });
          setFilteredTrainers(ft.length > 0 ? ft : trainers);

          // Filter rooms by branch
          const fr = rooms.filter((r) => {
            if (r.branches && r.branches.length > 0) {
              return r.branches.includes(branch);
            }
            return true;
          });
          setFilteredRooms(fr.length > 0 ? fr : rooms);
        } else {
          setFilteredTrainers(trainers);
          setFilteredRooms(rooms);
        }
      }
    } else if (reservationType === "demo" && selectedBranch) {
      // For demo, filter trainers and rooms by selected branch
      const ft = trainers.filter((t) => {
        if (t.branches && t.branches.length > 0) {
          return t.branches.includes(selectedBranch);
        }
        return true;
      });
      setFilteredTrainers(ft.length > 0 ? ft : trainers);

      const fr = rooms.filter((r) => {
        if (r.branches && r.branches.length > 0) {
          return r.branches.includes(selectedBranch);
        }
        return true;
      });
      setFilteredRooms(fr.length > 0 ? fr : rooms);
    } else {
      setFilteredTrainers(trainers);
      setFilteredRooms(rooms);
    }
  }, [selectedPackageId, packages, trainers, rooms, reservationType, selectedBranch]);

  // Auto calculate end time based on package duration or demo duration
  useEffect(() => {
    if (selectedTime) {
      let duration = 0;
      
      if (reservationType === "package" && selectedPackageInfo?.session_duration) {
        duration = selectedPackageInfo.session_duration;
      } else if (reservationType === "demo" && selectedBranch) {
        // Get demo duration for this branch (default 30 minutes)
        duration = branchDemoDurations[selectedBranch] || 30;
      }
      
      if (duration > 0) {
        const [hours, mins] = selectedTime.split(":").map(Number);
        const startMinutes = hours * 60 + mins;
        const endMinutes = startMinutes + duration;
        const endHours = Math.floor(endMinutes / 60);
        const endMins = endMinutes % 60;
        setEndTime(`${String(endHours).padStart(2, "0")}:${String(endMins).padStart(2, "0")}`);
      }
    }
  }, [selectedTime, selectedPackageInfo, reservationType, selectedBranch, branchDemoDurations]);

  // Derive training type from package or reservation type
  const trainingType = reservationType === "demo" 
    ? "demo" 
    : selectedPackageInfo?.session_type === "Özel Ders" || selectedPackageInfo?.session_type === "Birebir"
      ? "one_on_one"
      : selectedPackageInfo?.session_type === "Düet"
        ? "duet"
        : selectedPackageInfo?.session_type === "Grup Dersi"
          ? "group"
          : "one_on_one";

  // Check room availability
  useEffect(() => {
    const checkRoom = async () => {
      if (!selectedRoom || !selectedDate || !selectedTime) {
        setRoomAvailability(null);
        return;
      }
      try {
        let url = `${API}/rooms/${selectedRoom}/availability?date=${selectedDate}&time=${selectedTime}`;
        if (endTime) url += `&end_time=${endTime}`;
        if (trainingType) url += `&training_type=${encodeURIComponent(trainingType)}`;
        const response = await fetch(url, { credentials: "include" });
        if (response.ok) {
          setRoomAvailability(await response.json());
        }
      } catch (error) {
        console.error("Error checking room availability:", error);
      }
    };
    checkRoom();
  }, [selectedRoom, selectedTime, endTime, selectedDate, trainingType]);

  // Check trainer availability
  useEffect(() => {
    const checkTrainer = async () => {
      if (!selectedTrainer || !selectedDate || !selectedTime) {
        setTrainerAvailability(null);
        return;
      }
      try {
        let url = `${API}/trainers/${selectedTrainer}/availability?date=${selectedDate}&time=${selectedTime}`;
        if (endTime) url += `&end_time=${endTime}`;
        if (trainingType) url += `&training_type=${encodeURIComponent(trainingType)}`;
        if (selectedRoom) url += `&room_id=${encodeURIComponent(selectedRoom)}`;
        const response = await fetch(url, { credentials: "include" });
        if (response.ok) {
          setTrainerAvailability(await response.json());
        }
      } catch (error) {
        console.error("Error checking trainer availability:", error);
      }
    };
    checkTrainer();
  }, [selectedTrainer, selectedTime, endTime, selectedDate, trainingType, selectedRoom]);

  // Check if end time exceeds business closing time
  const isEndTimeWithinBusinessHours = (date, endTimeValue) => {
    if (!date || !endTimeValue || !businessWorkingHours) return true;
    
    // Get day of week (0 = Sunday, 1 = Monday, etc.)
    const dateObj = new Date(date);
    const dayIndex = dateObj.getDay();
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[dayIndex];
    
    const dayHours = businessWorkingHours[dayName];
    if (!dayHours || dayHours.closed) return false; // Business closed
    
    const closingTime = dayHours.close || dayHours.end || "22:00";
    
    // Compare times
    const [endH, endM] = endTimeValue.split(':').map(Number);
    const [closeH, closeM] = closingTime.split(':').map(Number);
    
    const endMinutes = endH * 60 + endM;
    const closeMinutes = closeH * 60 + closeM;
    
    return endMinutes <= closeMinutes;
  };
  
  // Get business closing time for a specific date
  const getBusinessClosingTime = (date) => {
    if (!date || !businessWorkingHours) return "22:00";
    
    const dateObj = new Date(date);
    const dayIndex = dateObj.getDay();
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[dayIndex];
    
    const dayHours = businessWorkingHours[dayName];
    if (!dayHours || dayHours.closed) return null;
    
    return dayHours.close || dayHours.end || "22:00";
  };

  // Helper function to check if a date/time is valid for reservation
  const isTimeSlotValid = (date, time) => {
    if (!date || !time) return true;
    
    const now = new Date();
    const minAdvanceHours = businessSettings.reservation_settings?.min_advance_booking || 
                           businessSettings.min_advance_booking || 2;
    
    const [hours, minutes] = time.split(':').map(Number);
    const selectedDateTime = new Date(date);
    selectedDateTime.setHours(hours, minutes, 0, 0);
    
    const minAllowedTime = new Date(now.getTime() + (minAdvanceHours * 60 * 60 * 1000));
    
    return selectedDateTime >= minAllowedTime;
  };

  const handleSubmit = async () => {
    // Validations
    if (!selectedMembership || !selectedBranch || !reservationType || !selectedTrainer || !selectedRoom || !selectedTime) {
      toast.error(t('common:fillAllFields'));
      return;
    }
    
    if (reservationType === "package" && !selectedPackageId) {
      toast.error(t('packages:selectPackage'));
      return;
    }

    // Kapanış saati kontrolü
    if (!isEndTimeWithinBusinessHours(selectedDate, endTime)) {
      const closingTime = getBusinessClosingTime(selectedDate);
      toast.error(`Bitiş saati işletme kapanış saatini (${closingTime}) aşıyor`);
      return;
    }

    // Min advance booking kontrolü
    if (!isTimeSlotValid(selectedDate, selectedTime)) {
      const minAdvanceHours = businessSettings.reservation_settings?.min_advance_booking || 
                             businessSettings.min_advance_booking || 2;
      const minAdvanceText = minAdvanceHours < 1 ? `${minAdvanceHours * 60} dakika` : `${minAdvanceHours} saat`;
      toast.error(`Rezervasyon en az ${minAdvanceText} önceden yapılmalıdır`);
      return;
    }

    if (trainerAvailability && !trainerAvailability.is_available) {
      toast.error(t('reservations:trainerNotAvailable'));
      return;
    }

    // Oda müsaitlik kontrolü - mk_available kullan
    if (roomAvailability) {
      if (roomAvailability.is_locked) {
        toast.error(t('reservations:roomLockedAtTime'));
        return;
      }
      const mk = roomAvailability.mk_available ?? roomAvailability.available_capacity ?? 0;
      if (mk <= 0) {
        toast.error(t("common:toast_odaMusaitDegil"));
        return;
      }
    }

    setSubmitting(true);
    try {
      const requestData = {
        business_id: selectedMembership.business_id,
        member_id: selectedMembership.member_id,
        trainer_id: selectedTrainer,
        room_id: selectedRoom,
        branch: selectedBranch,
        date: selectedDate,
        start_time: selectedTime,
        end_time: endTime,
        is_demo: reservationType === "demo",
        allow_shared: true,
        notes: notes,
      };
      
      // Add package info if it's a package reservation
      if (reservationType === "package" && selectedPackageId) {
        requestData.member_package_id = selectedPackageId;
        requestData.training_type = selectedPackageInfo?.session_type === "Özel Ders" ? "one_on_one" : "group";
        requestData.payment_type = "package";
      } else {
        // Demo reservation
        requestData.training_type = "demo";
        requestData.payment_type = "demo";
      }
      
      // Use the member-request endpoint which writes to reservations collection
      const response = await fetch(`${API}/reservations/member-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(requestData),
      });

      if (response.ok) {
        toast.success(t("common:reservationCreated"));
        // Reset form
        setSelectedPackageId("");
        setSelectedPackageInfo(null);
        setSelectedTrainer("");
        setSelectedRoom("");
        setSelectedTime("");
        setEndTime("");
        setNotes("");
      } else {
        const error = await response.json();
        const errorMsg = typeof error.detail === "string"
          ? error.detail
          : Array.isArray(error.detail)
            ? error.detail.map((e) => e.msg || e).join(", ")
            : t("common:requestCreateFailed");
        toast.error(errorMsg);
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  if (memberships.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardContent className="p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-grey opacity-50" />
          <h3 className="text-lg font-semibold text-navy mb-2">{t("common:uyeliginizBulunmuyor")}</h3>
          <p className="text-grey">Rezervasyon talebi oluşturmak için önce bir işletmeye üye olmanız gerekiyor.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading font-bold text-xl text-navy">{t("common:rezervasyonTalebiOlustur")}</h2>
        <p className="text-grey text-sm">Antrenman için talep oluşturun, işletme onayladıktan sonra rezervasyonunuz kesinleşir.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form */}
        <div className="lg:col-span-2 space-y-4">
          {/* Membership Selection */}
          {memberships.length > 1 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  1. İşletme Seçin
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {memberships.map((m) => (
                    <div
                      key={m.member_id}
                      onClick={() => handleMembershipSelect(m)}
                      className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        selectedMembership?.member_id === m.member_id
                          ? "border-navy bg-navy/5"
                          : "border-slate-200 hover:border-slate-300"
                      }`}
                    >
                      <p className="font-semibold text-navy">{m.business_name}</p>
                      <p className="text-sm text-grey">{m.city}, {m.district}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {selectedMembership && (
            <>
              {/* Branch Selection */}
              <Card className="border-0 shadow-sm">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Dumbbell className="w-4 h-4" />
                    {memberships.length > 1 ? "2." : "1."} Branş Seçin
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {businessBranches.length > 0 ? (
                    <Select value={selectedBranch} onValueChange={setSelectedBranch}>
                      <SelectTrigger className="rounded-xl" data-testid="member-branch-select">
                        <SelectValue placeholder={t("common:ph_bransSecin")} />
                      </SelectTrigger>
                      <SelectContent>
                        {businessBranches.map((branch) => (
                          <SelectItem key={branch} value={branch}>
                            {getLocalizedBranch(branch)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <p className="text-grey text-sm">Bu işletmede tanımlı branş bulunmuyor.</p>
                  )}
                </CardContent>
              </Card>

              {/* Reservation Type: Package or Demo */}
              {selectedBranch && (
                <Card className="border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {memberships.length > 1 ? "3." : "2."} Talep Türü
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div
                        onClick={() => {
                          setReservationType("package");
                          setSelectedPackageId("");
                        }}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                          reservationType === "package"
                            ? "border-navy bg-navy/5"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="reservation-type-package"
                      >
                        <p className="font-semibold text-navy">{t("packages:package")}</p>
                        <p className="text-xs text-grey">{t("packages:mevcutPaketinizdenKullanin")}</p>
                      </div>
                      <div
                        onClick={() => {
                          setReservationType("demo");
                          setSelectedPackageId("");
                          setSelectedPackageInfo(null);
                        }}
                        className={`p-4 rounded-xl border-2 cursor-pointer transition-all text-center ${
                          reservationType === "demo"
                            ? "border-green-600 bg-green-50"
                            : "border-slate-200 hover:border-slate-300"
                        }`}
                        data-testid="reservation-type-demo"
                      >
                        <p className="font-semibold text-green-700">Demo</p>
                        <p className="text-xs text-grey">Deneme dersi talep edin</p>
                      </div>
                    </div>

                    {/* Package selection if type is package */}
                    {reservationType === "package" && (
                      <div>
                        <Label>{t('members:selectPackage')}</Label>
                        {filteredPackages.length > 0 ? (
                          <Select value={selectedPackageId} onValueChange={setSelectedPackageId}>
                            <SelectTrigger className="rounded-xl mt-1" data-testid="member-package-select">
                              <SelectValue placeholder={t("packages:ph_paketSecin")} />
                            </SelectTrigger>
                            <SelectContent>
                              {filteredPackages.map((p) => (
                                <SelectItem key={p.member_package_id} value={p.member_package_id}>
                                  {p.package_name} - Kalan: {p.remaining_sessions} ders
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-amber-600 text-sm mt-1">
                            Bu branşta aktif paketiniz bulunmuyor. Demo talep edebilirsiniz.
                          </p>
                        )}
                      </div>
                    )}

                    {/* Demo info */}
                    {reservationType === "demo" && (
                      <div className="bg-green-50 p-3 rounded-xl">
                        <p className="text-sm text-green-700">
                          <strong>{selectedBranch}</strong> branşı için demo dersi talebi oluşturacaksınız.
                          <br />
                          <span className="text-xs">
                            Demo süresi: {branchDemoDurations[selectedBranch] || 30} dakika
                          </span>
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Trainer & Room Selection - show after type selection */}
              {((reservationType === "package" && selectedPackageId) || reservationType === "demo") && (
                <>
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <User className="w-4 h-4" />
                        {memberships.length > 1 ? "4." : "3."} Antrenör ve Oda
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>{t('common:trainer')}</Label>
                        <Select value={selectedTrainer} onValueChange={setSelectedTrainer}>
                          <SelectTrigger className="rounded-xl" data-testid="member-trainer-select">
                            <SelectValue placeholder={t("common:selectTrainer")} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredTrainers.map((t) => (
                              <SelectItem key={t.trainer_id} value={t.trainer_id}>
                                {t.name} {t.surname}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t('common:room')}</Label>
                        <Select value={selectedRoom} onValueChange={setSelectedRoom}>
                          <SelectTrigger className="rounded-xl" data-testid="member-room-select">
                            <SelectValue placeholder={t("common:ph_odaSecin")} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredRooms.map((r) => (
                              <SelectItem key={r.room_id} value={r.room_id}>
                                {r.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Date & Time Selection */}
                  <Card className="border-0 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {memberships.length > 1 ? "4." : "3."} Tarih ve Saat
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>{t('common:date')}</Label>
                        <Select value={selectedDate} onValueChange={setSelectedDate}>
                          <SelectTrigger className="rounded-xl" data-testid="member-date-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {dateOptions.map((d) => (
                              <SelectItem key={d.value} value={d.value}>
                                {d.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>{t("common:baslangicSaati")}</Label>
                          <TimePicker
                            value={selectedTime}
                            onChange={setSelectedTime}
                            placeholder={t('common:time')}
                            minHour={8}
                            maxHour={22}
                            minuteStep={5}
                            dataTestId="member-time"
                          />
                        </div>
                        <div>
                          <Label>
                            Bitiş Saati 
                            {reservationType === "package" && selectedPackageInfo?.session_duration && (
                              <span className="text-xs text-grey ml-1">(Önerilen: {selectedPackageInfo.session_duration} dk)</span>
                            )}
                            {reservationType === "demo" && selectedBranch && (
                              <span className="text-xs text-grey ml-1">(Demo: {branchDemoDurations[selectedBranch] || 30} dk)</span>
                            )}
                          </Label>
                          <div className="mt-1">
                            <TimePicker
                              value={endTime}
                              onChange={setEndTime}
                              placeholder={t("common:ph_bitisSaati")}
                              minHour={8}
                              maxHour={23}
                              minuteStep={5}
                            />
                          </div>
                          {selectedTime && endTime && endTime <= selectedTime && (
                            <p className="text-xs text-red-500 mt-1">
                              ⚠️ Bitiş saati başlangıçtan sonra olmalı
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Availability Status */}
                      {(roomAvailability || trainerAvailability) && (
                        <div className="p-3 bg-slate-50 rounded-xl space-y-2">
                          <p className="font-medium text-navy text-sm">{t("common:availabilityStatus")}</p>
                          {/* ODA MÜSAİTLİĞİ - MK formatı */}
                          {roomAvailability && (
                            <div className={`flex items-center gap-2 text-sm ${
                              roomAvailability.is_locked 
                                ? 'text-red-600' 
                                : (roomAvailability.mk_available ?? roomAvailability.available_capacity) > 0 
                                  ? 'text-green-600' 
                                  : 'text-red-600'
                            }`}>
                              {roomAvailability.is_locked || (roomAvailability.mk_available ?? roomAvailability.available_capacity) === 0 
                                ? <AlertCircle className="w-4 h-4" /> 
                                : <CheckCircle2 className="w-4 h-4" />}
                              <span>
                                Oda: {roomAvailability.is_locked 
                                  ? "Kilitli" 
                                  : (roomAvailability.mk_available ?? roomAvailability.available_capacity) > 0 
                                    ? `Müsait (${roomAvailability.mk_available ?? roomAvailability.available_capacity} kişi boşluk var)`
                                    : t("common:notAvailable")}
                              </span>
                            </div>
                          )}
                          {/* ANTRENÖR MÜSAİTLİĞİ - ÖA formatı */}
                          {trainerAvailability && (
                            <div className={`flex items-center gap-2 text-sm ${trainerAvailability.is_available ? "text-green-600" : "text-red-600"}`}>
                              {trainerAvailability.is_available ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                              <span>
                                Antrenör: {trainerAvailability.is_available 
                                  ? (() => {
                                      // ATM > 1 ise açıklama ile göster (Düet, Trio vb.)
                                      const info = trainerAvailability.training_type_info;
                                      if (info && info.max_participants > 1) {
                                        return `Müsait (${info.type} ders için ${info.remaining ?? (info.max_participants - info.current_count)} kişi boşluk var)`;
                                      }
                                      // ATM = 1 ise sadece t("common:available") (birebir ders)
                                      return t("common:available");
                                    })()
                                  : (trainerAvailability.reason || t('reservations:notAvailable'))}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Notes */}
                  <Card className="border-0 shadow-sm">
                    <CardContent className="pt-4">
                      <Label>Not (opsiyonel)</Label>
                      <Textarea
                        placeholder="Ek notlar..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="rounded-xl mt-1"
                        data-testid="member-request-notes"
                      />
                    </CardContent>
                  </Card>
                </>
              )}
            </>
          )}
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <Card className="border-0 shadow-sm sticky top-4">
            <CardHeader>
              <CardTitle className="text-base">{t("reservations:requestSummary")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {selectedMembership ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-grey">İşletme:</span>
                    <span className="font-medium">{selectedMembership.business_name}</span>
                  </div>
                  {selectedBranch && (
                    <div className="flex justify-between">
                      <span className="text-grey">Branş:</span>
                      <span className="font-medium">{selectedBranch}</span>
                    </div>
                  )}
                  {reservationType && (
                    <div className="flex justify-between">
                      <span className="text-grey">Talep Türü:</span>
                      <Badge variant={reservationType === "demo" ? "secondary" : "default"} className={reservationType === "demo" ? "bg-green-100 text-green-700" : ""}>
                        {reservationType === "demo" ? t('reservations:demo') : t('packages:package')}
                      </Badge>
                    </div>
                  )}
                  {reservationType === "package" && selectedPackageInfo && (
                    <div className="flex justify-between">
                      <span className="text-grey">Paket:</span>
                      <span className="font-medium">{selectedPackageInfo.package_name}</span>
                    </div>
                  )}
                  {reservationType === "demo" && selectedBranch && (
                    <div className="flex justify-between">
                      <span className="text-grey">Demo Süresi:</span>
                      <span>{branchDemoDurations[selectedBranch] || 30} dakika</span>
                    </div>
                  )}
                  {selectedTrainer && (
                    <div className="flex justify-between">
                      <span className="text-grey">Antrenör:</span>
                      <span>{filteredTrainers.find(t => t.trainer_id === selectedTrainer)?.name || "-"}</span>
                    </div>
                  )}
                  {selectedRoom && (
                    <div className="flex justify-between">
                      <span className="text-grey">Oda:</span>
                      <span>{filteredRooms.find(r => r.room_id === selectedRoom)?.name || "-"}</span>
                    </div>
                  )}
                  {selectedDate && selectedTime && (
                    <div className="flex justify-between">
                      <span className="text-grey">Tarih/Saat:</span>
                      <span>{selectedDate} {selectedTime} - {endTime}</span>
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <Button
                      className="w-full bg-orange-500 hover:bg-orange-600 rounded-xl"
                      onClick={handleSubmit}
                      disabled={
                        !selectedBranch || 
                        !reservationType || 
                        (reservationType === "package" && !selectedPackageId) ||
                        !selectedTrainer || 
                        !selectedRoom || 
                        !selectedTime || 
                        submitting || 
                        (trainerAvailability && !trainerAvailability.is_available) || 
                        (roomAvailability && roomAvailability.is_locked)
                      }
                      data-testid="submit-member-request-btn"
                    >
                      {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      {submitting ? t("common:submitting") : t("common:createRequest")}
                    </Button>
                    <p className="text-xs text-grey text-center mt-2">
                      İşletme onayladıktan sonra rezervasyonunuz kesinleşir.
                    </p>
                  </div>
                </>
              ) : (
                <p className="text-grey">Talep oluşturmak için işletme ve branş seçin.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
